import { range } from "lodash";
import { TRIGGER_TYPE_DEPENDENCY, TRIGGER_TYPE_TIMING } from "../models";

const REFERENCE_EVENT_FIELD = (name: any) => ({
  type: "SELECT",
  label: "Event",
  placeholder: "Event",
  name: `${name}.data.referenceEvent`,
  options: [
    { label: "Start Date", value: "start_date" },
    { label: "Immediate", value: "immediate" },
  ],
});

const OFFSET_DIRECTION_FIELD = (name: any) => ({
  type: "SELECT",
  label: "Before/After/On",
  placeholder: "Before/After/On",
  name: `${name}.data.offsetDirection`,
  options: [
    { label: "Before", value: "before" },
    { label: "After", value: "after" },
    { label: "On", value: "exact" },
  ],
});

export type CombinedOffsetEventOption = {
  label: string;
  value: { offsetDirection: string; referenceEvent: string };
};

// Single source of truth for the timing picker — a fork of this list is how the
// due-field guard regressed once already (#1032, then 28446bc05).
export function combinedOffsetEventOptions(fieldName: string): CombinedOffsetEventOption[] {
  const isDueField = fieldName.toLowerCase().includes("due");

  return [
    // For due dates, "immediate" resolves to end of the day the task starts
    { label: isDueField ? "Due Immediately" : "After Kickoff", value: { offsetDirection: "exact", referenceEvent: "immediate" } },
    { label: "Before Start Date", value: { offsetDirection: "before", referenceEvent: "start_date" } },
    { label: "On Start Date", value: { offsetDirection: "exact", referenceEvent: "start_date" } },
    { label: "After Start Date", value: { offsetDirection: "after", referenceEvent: "start_date" } },
  ];
}

const COMBINED_OFFSET_EVENT_FIELD = (name: any) => ({
  type: "SELECT",
  label: "When",
  placeholder: "When",
  options: combinedOffsetEventOptions(String(name)),
});

const OFFSET_VALUE_FIELD = (name: any) => ({
  type: "SELECT",
  label: "Timing",
  placeholder: "Number",
  name: `${name}.data.offsetValue`,
  options: range(1, 120).map((value) => ({ label: value.toString(), value })),
});

const OFFSET_UNIT_FIELD = (name: any) => ({
  type: "SELECT",
  label: "Day/Week/Month",
  placeholder: "Day/Week/Month",
  name: `${name}.data.offsetUnit`,
  options: [
    { label: "Day(s)", value: "day" },
    { label: "Week(s)", value: "week" },
    { label: "Month(s)", value: "month" },
    { label: "Specific Day", value: "specific_day" },
  ],
});

const DAY_OF_WEEK_FIELD = (name: any) => ({
  type: "SELECT",
  label: "Day of Week",
  placeholder: "Day of Week",
  name: `${name}.data.dayOfWeek`,
  options: [
    { label: "Monday", value: "monday" },
    { label: "Tuesday", value: "tuesday" },
    { label: "Wednesday", value: "wednesday" },
    { label: "Thursday", value: "thursday" },
    { label: "Friday", value: "friday" },
    { label: "Saturday", value: "saturday" },
    { label: "Sunday", value: "sunday" },
  ],
});

const TIME_OF_DAY_FIELD = (name: any) => ({
  type: "TIME",
  label: "Send At",
  placeholder: "Send At",
  name: `${name}.data.timeOfDay`,
});

const TIMEZONE_TYPE = (name: any) => ({
  type: "SELECT",
  label: "Timezone Source",
  name: `${name}.data.timezoneType`,
  options: [
    { label: "Recipient's Timezone", value: "target_timezone" },
    { label: "Company Default Timezone", value: "company_default_timezone" },
    { label: "Specific Timezone", value: "specific_timezone" },
  ],
});

const SPECIFIC_TIMEZONE = (name: any) => ({
  type: "SELECT",
  label: "Specific Timezone",
  placeholder: "Specific Timezone",
  name: `${name}.data.specificTimezone`,
});

function schedulingFields(name: any) {
  return {
    combinedOffsetEvent: COMBINED_OFFSET_EVENT_FIELD(name),
    offsetDirection: OFFSET_DIRECTION_FIELD(name),
    referenceEvent: REFERENCE_EVENT_FIELD(name),
    offsetValue: OFFSET_VALUE_FIELD(name),
    offsetUnit: OFFSET_UNIT_FIELD(name),
    dayOfWeek: DAY_OF_WEEK_FIELD(name),
    timeOfDay: TIME_OF_DAY_FIELD(name),
    timezoneType: TIMEZONE_TYPE(name),
    specificTimezone: SPECIFIC_TIMEZONE(name),
  };
}

const TRIGGER_TYPE_FIELD = {
  type: "SELECT",
  label: "Trigger Type",
  options: [
    { label: "Time Based", value: TRIGGER_TYPE_TIMING },
    { label: "On Task Completion", value: TRIGGER_TYPE_DEPENDENCY },
  ],
};

export {
  schedulingFields,
  TRIGGER_TYPE_FIELD,
};
